/**
 * PulseMeasure™ Admin Dashboard — SocialPulse Intelligence™ Phase 1.
 *
 * Tabs: Network overview | Per-site drill-down | Sites registry.
 * Data comes from `/api/intelligence/dashboard` and
 * `/api/intelligence/sites/{slug}/dashboard`.
 */
import { useEffect, useState, useCallback } from "react";
import { http } from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, TrendingUp, TrendingDown, Sparkles, Copy, RefreshCw, Plus } from "lucide-react";

export default function IntelligenceAdminPage() {
  const [tab, setTab] = useState("network"); // network|site|sites
  const [selectedSite, setSelectedSite] = useState(null);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="intelligence-admin-page">
      <div className="mb-6">
        <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-emerald-700 mb-2">
          <Sparkles className="w-3.5 h-3.5" />PulseMeasure™
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">SocialPulse Intelligence</h1>
        <p className="text-slate-600 mt-1 text-sm">
          Measurement, attribution, and cross-site journeys for the SocialPulse network + client sites.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200">
        <TabBtn testid="intel-tab-network" active={tab === "network"} onClick={() => setTab("network")}>Network</TabBtn>
        <TabBtn testid="intel-tab-site" active={tab === "site"} onClick={() => setTab("site")}>Per-site</TabBtn>
        <TabBtn testid="intel-tab-journeys" active={tab === "journeys"} onClick={() => setTab("journeys")}>Journeys</TabBtn>
        <TabBtn testid="intel-tab-sites" active={tab === "sites"} onClick={() => setTab("sites")}>Sites registry</TabBtn>
      </div>

      {tab === "network" && <DashboardView slug={null} />}
      {tab === "site" && <PerSiteView selected={selectedSite} onSelect={setSelectedSite} />}
      {tab === "journeys" && <JourneysView />}
      {tab === "sites" && <SitesRegistry />}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${active ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}
    >{children}</button>
  );
}

function IntelAuthOrError({ err }) {
  const isAuth = /auth|unauth|admin.*required|forbidden|401|403/i.test(String(err));
  if (isAuth) {
    return (
      <div className="max-w-md mx-auto text-center py-16" data-testid="intel-auth-required">
        <div className="text-lg font-semibold mb-2">Admin access required</div>
        <p className="text-sm text-slate-600 mb-4">Sign in with a SocialPulse admin account to view the intelligence dashboard.</p>
        <a href="/login?next=/admin/intelligence" className="inline-block px-4 py-2 bg-slate-900 text-white rounded text-sm">Sign in</a>
      </div>
    );
  }
  return <div className="text-red-600 py-8">{err}</div>;
}

function DashboardView({ slug }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [period, setPeriod] = useState(7);

  const load = useCallback(async () => {
    setData(null); setErr("");
    try {
      const url = slug
        ? `/intelligence/sites/${slug}/dashboard?period_days=${period}`
        : `/intelligence/dashboard?period_days=${period}`;
      const { data } = await http.get(url);
      setData(data);
    } catch (e) {
      setErr(apiErrorMessage(e, "Couldn't load dashboard."));
    }
  }, [slug, period]);

  useEffect(() => { load(); }, [load]);

  if (err) {
    return <IntelAuthOrError err={err} />;
  }
  if (!data) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-slate-500">
          {slug ? `Site: ${slug}` : "All sites"} · Last {period} days
        </div>
        <div className="flex gap-1">
          {[7, 30, 90].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1 text-xs rounded ${period === p ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}
            >{p}d</button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 mb-6">
        {data.tiles.map(t => (
          <Card key={t.key} className="p-4" data-testid={`intel-tile-${t.key}`}>
            <div className="text-xs text-slate-500 uppercase tracking-wide">{t.label}</div>
            <div className="text-2xl font-semibold mt-1">
              {typeof t.value === "number" && !Number.isInteger(t.value) ? t.value.toFixed(1) : t.value.toLocaleString()}
              {t.key === "conversion_rate" ? "%" : ""}
            </div>
            {t.delta_pct !== null && t.delta_pct !== undefined && (
              <div className={`text-xs mt-1 flex items-center gap-1 ${t.delta_pct >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                {t.delta_pct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {Math.abs(t.delta_pct).toFixed(1)}%
              </div>
            )}
          </Card>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Card className="p-4">
          <h3 className="text-sm font-semibold mb-3">Events per day</h3>
          <MiniSparkline points={data.timeseries} />
        </Card>
        <Card className="p-4">
          <h3 className="text-sm font-semibold mb-3">Top events</h3>
          <BarList items={data.top_events.map(e => ({ label: e.event, value: e.count }))} />
        </Card>
        <Card className="p-4 sm:col-span-2">
          <h3 className="text-sm font-semibold mb-3">Top source sites (cross-site referrals)</h3>
          {data.top_sources.length === 0
            ? <div className="text-sm text-slate-500">No cross-site referrals yet in this period.</div>
            : <BarList items={data.top_sources.map(s => ({ label: s.source_site, value: s.count }))} />}
        </Card>
      </div>
    </div>
  );
}

function MiniSparkline({ points }) {
  if (!points || points.length === 0) return <div className="text-sm text-slate-500 py-4">No data yet.</div>;
  const max = Math.max(1, ...points.map(p => p.count));
  return (
    <div className="flex items-end gap-1 h-24">
      {points.map(p => (
        <div key={p.date} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${p.date}: ${p.count}`}>
          <div className="w-full bg-emerald-500 rounded-t" style={{ height: `${(p.count / max) * 100}%`, minHeight: 2 }} />
          <div className="text-[10px] text-slate-500 truncate">{p.date.slice(5)}</div>
        </div>
      ))}
    </div>
  );
}

function BarList({ items }) {
  const max = Math.max(1, ...items.map(i => i.value));
  return (
    <div className="space-y-1.5">
      {items.map(i => (
        <div key={i.label} className="flex items-center gap-2 text-sm">
          <div className="w-40 truncate text-slate-700">{i.label}</div>
          <div className="flex-1 bg-slate-100 rounded h-4 relative">
            <div className="absolute inset-y-0 left-0 bg-emerald-500 rounded" style={{ width: `${(i.value / max) * 100}%` }} />
          </div>
          <div className="w-12 text-right text-slate-600 tabular-nums">{i.value.toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
}

function PerSiteView({ selected, onSelect }) {
  const [sites, setSites] = useState([]);
  useEffect(() => { http.get("/intelligence/sites").then(r => setSites(r.data.sites || [])); }, []);
  if (!selected) {
    return (
      <div>
        <div className="text-sm text-slate-500 mb-3">Pick a site to see its dashboard.</div>
        <div className="grid gap-2 sm:grid-cols-3">
          {sites.map(s => (
            <Card key={s.slug} className="p-3 cursor-pointer hover:border-slate-500 transition"
              onClick={() => onSelect(s.slug)} data-testid={`site-pick-${s.slug}`}>
              <div className="font-medium">{s.name}</div>
              <div className="text-xs text-slate-500">{s.domain}</div>
              <div className="mt-1 flex gap-1"><Badge variant="outline" className="text-[10px]">{s.vertical || "—"}</Badge>{s.owner_type === "client" && <Badge className="text-[10px]">client</Badge>}</div>
            </Card>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="mb-3">
        <Button variant="outline" size="sm" onClick={() => onSelect(null)}>← All sites</Button>
      </div>
      <DashboardView slug={selected} />
    </div>
  );
}

function SitesRegistry() {
  const [sites, setSites] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newSite, setNewSite] = useState({ slug: "", name: "", domain: "", vertical: "", country: "", owner_type: "client", owner_email: "" });
  const [newKey, setNewKey] = useState(null);
  const [err, setErr] = useState("");

  const load = () => http.get("/intelligence/sites").then(r => setSites(r.data.sites || []));
  useEffect(() => { load(); }, []);

  const add = async () => {
    setErr("");
    if (!newSite.slug.trim() || !newSite.name.trim() || !newSite.domain.trim()) {
      setErr("Slug, name and domain are required.");
      return;
    }
    if (!/^[a-z0-9\-]+$/.test(newSite.slug)) {
      setErr("Slug must be lowercase letters, numbers and hyphens only.");
      return;
    }
    try {
      const { data } = await http.post("/intelligence/sites", newSite);
      setNewKey({ slug: data.site.slug, key: data.site.api_key });
      setNewSite({ slug: "", name: "", domain: "", vertical: "", country: "", owner_type: "client", owner_email: "" });
      await load();
    } catch (e) { setErr(apiErrorMessage(e, "Failed to register site.")); }
  };

  const rotate = async (slug) => {
    if (!window.confirm(`Rotate API key for ${slug}? The old key will stop working immediately.`)) return;
    try {
      const { data } = await http.post(`/intelligence/sites/${slug}/rotate`);
      setNewKey({ slug, key: data.api_key });
    } catch (e) { setErr(apiErrorMessage(e, "Failed to rotate key.")); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-slate-500">{sites.length} site(s) registered</div>
        <Button size="sm" onClick={() => setShowAdd(!showAdd)} data-testid="add-site-btn">
          <Plus className="w-4 h-4 mr-1.5" />Add client site
        </Button>
      </div>

      {newKey && (
        <Card className="p-4 bg-amber-50 border-amber-200 mb-4">
          <div className="text-sm font-semibold text-amber-900 mb-2">
            API key for <code>{newKey.slug}</code> — copy this now, we won't show it again:
          </div>
          <div className="flex items-center gap-2 bg-white border border-amber-300 rounded p-2 font-mono text-xs break-all">
            {newKey.key}
            <button onClick={() => navigator.clipboard.writeText(newKey.key)} className="ml-auto flex-shrink-0">
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <Button variant="ghost" size="sm" className="mt-2" onClick={() => setNewKey(null)}>Dismiss</Button>
        </Card>
      )}

      {showAdd && (
        <Card className="p-5 mb-4 space-y-3">
          <h3 className="font-semibold">Register a client site</h3>
          <div className="grid grid-cols-2 gap-3">
            <TextField testid="site-form-slug" label="Slug (unique, lowercase)" value={newSite.slug} onChange={v => setNewSite({ ...newSite, slug: v })} />
            <TextField testid="site-form-name" label="Name" value={newSite.name} onChange={v => setNewSite({ ...newSite, name: v })} />
            <TextField testid="site-form-domain" label="Domain" value={newSite.domain} onChange={v => setNewSite({ ...newSite, domain: v })} />
            <TextField testid="site-form-vertical" label="Vertical" value={newSite.vertical} onChange={v => setNewSite({ ...newSite, vertical: v })} />
            <TextField testid="site-form-country" label="Country (2-letter)" value={newSite.country} onChange={v => setNewSite({ ...newSite, country: v })} />
            <TextField testid="site-form-owner-email" label="Owner email" value={newSite.owner_email} onChange={v => setNewSite({ ...newSite, owner_email: v })} />
          </div>
          {err && <div className="text-sm text-red-600">{err}</div>}
          <div className="flex gap-2">
            <Button onClick={add} data-testid="register-site-btn">Register + mint key</Button>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
          </div>
        </Card>
      )}

      <Card className="divide-y">
        {sites.map(s => (
          <div key={s.slug} className="p-3 flex items-center gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">{s.name}</span>
                <Badge variant="outline" className="text-[10px]">{s.slug}</Badge>
                {s.owner_type === "client" && <Badge className="text-[10px]">client</Badge>}
                {!s.active && <Badge variant="destructive" className="text-[10px]">inactive</Badge>}
              </div>
              <div className="text-xs text-slate-500">{s.domain} · {s.vertical || "—"}{s.country ? ` · ${s.country}` : ""}</div>
              {s.key_prefix && <div className="text-[10px] text-slate-400 font-mono mt-0.5">{s.key_prefix}</div>}
            </div>
            <Button variant="ghost" size="sm" onClick={() => rotate(s.slug)} data-testid={`rotate-${s.slug}`}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </Card>
    </div>
  );
}

function TextField({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <input value={value} onChange={e => onChange(e.target.value)} data-testid={testid} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" />
    </div>
  );
}

// ─── Journeys tab (Phase 3) ────────────────────────────────────────────

function JourneysView() {
  const [journeys, setJourneys] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [period, setPeriod] = useState(30);
  const [convertedOnly, setConvertedOnly] = useState(false);
  const [minHops, setMinHops] = useState(2);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      http.get(`/intelligence/journeys?period_days=${period}&converted_only=${convertedOnly}&min_hops=${minHops}&limit=50`),
      http.get(`/intelligence/journeys-transitions?period_days=${period}`),
    ]).then(([j, m]) => {
      if (!alive) return;
      setJourneys(j.data.journeys || []);
      setMatrix(m.data);
    }).catch(e => setErr(apiErrorMessage(e, "Couldn't load journeys.")))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [period, convertedOnly, minHops]);

  if (err) return <IntelAuthOrError err={err} />;
  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div data-testid="intel-journeys-view">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="text-sm text-slate-500">
          {journeys.length} multi-site journeys · {matrix?.edges?.length || 0} site-to-site transitions
        </div>
        <div className="flex gap-3 items-center">
          <label className="text-xs flex items-center gap-1.5">
            <input type="checkbox" checked={convertedOnly} onChange={e => setConvertedOnly(e.target.checked)} data-testid="journeys-converted-only" />
            Converted only
          </label>
          <label className="text-xs flex items-center gap-1.5">
            Min hops
            <select value={minHops} onChange={e => setMinHops(Number(e.target.value))} className="border border-slate-300 rounded text-xs px-1.5 py-0.5">
              {[2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <div className="flex gap-1">
            {[7, 30, 90].map(p => (
              <button key={p} onClick={() => setPeriod(p)}
                className={`px-3 py-1 text-xs rounded ${period === p ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{p}d</button>
            ))}
          </div>
        </div>
      </div>

      {/* Transition matrix — Sankey-style flows */}
      <Card className="p-4 sm:p-5 mb-6" data-testid="journeys-transitions-card">
        <h3 className="text-sm font-semibold mb-3">Cross-site transitions</h3>
        {(!matrix?.edges || matrix.edges.length === 0)
          ? <div className="text-sm text-slate-500 py-6 text-center">No cross-site transitions yet in this period.</div>
          : <TransitionFlow matrix={matrix} />}
      </Card>

      {/* Journey list */}
      <Card className="p-4 sm:p-5" data-testid="journeys-list-card">
        <h3 className="text-sm font-semibold mb-3">Recent multi-site journeys</h3>
        {journeys.length === 0
          ? <div className="text-sm text-slate-500 py-6 text-center">No multi-site journeys detected yet. Journeys will appear once a visitor touches 2+ sites in one session.</div>
          : <div className="space-y-1">
              {journeys.map(j => (
                <button
                  key={j.session_id}
                  onClick={() => setSelected(j.session_id)}
                  data-testid={`journey-${j.session_id.slice(0, 8)}`}
                  className="w-full text-left p-3 rounded border border-slate-200 hover:border-slate-400 transition"
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 text-sm">
                      {j.sites.slice(0, 6).map((s, i) => (
                        <span key={i} className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px]">{s}</Badge>
                          {i < Math.min(j.sites.length, 6) - 1 && <span className="text-slate-400">→</span>}
                        </span>
                      ))}
                      {j.sites.length > 6 && <span className="text-xs text-slate-500">+{j.sites.length - 6} more</span>}
                    </div>
                    {j.converted && <Badge className="text-[10px] bg-emerald-600">converted</Badge>}
                  </div>
                  <div className="text-xs text-slate-500">
                    {j.events_count} events · {new Date(j.first_ts).toLocaleString()} → {new Date(j.last_ts).toLocaleString()}
                  </div>
                </button>
              ))}
            </div>}
      </Card>

      {selected && <JourneyDetailModal sessionId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function TransitionFlow({ matrix }) {
  const maxWeight = Math.max(1, ...matrix.edges.map(e => e.weight));
  return (
    <div className="space-y-1.5">
      {matrix.edges.slice(0, 15).map((e, i) => (
        <div key={i} className="flex items-center gap-2 text-sm">
          <div className="w-32 truncate text-right"><Badge variant="outline" className="text-[10px]">{e.from}</Badge></div>
          <div className="flex-1 flex items-center gap-1">
            <div className="flex-1 h-2 bg-slate-100 rounded relative">
              <div className="absolute inset-y-0 left-0 bg-emerald-500 rounded" style={{ width: `${(e.weight / maxWeight) * 100}%` }} />
            </div>
            <span className="text-slate-400 text-xs">→</span>
          </div>
          <div className="w-32 truncate"><Badge variant="outline" className="text-[10px]">{e.to}</Badge></div>
          <div className="w-12 text-right text-slate-600 tabular-nums text-xs">{e.weight}</div>
        </div>
      ))}
      {matrix.edges.length > 15 && (
        <div className="text-xs text-slate-500 text-center pt-2">
          + {matrix.edges.length - 15} more transitions
        </div>
      )}
    </div>
  );
}

function JourneyDetailModal({ sessionId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    http.get(`/intelligence/journeys/${sessionId}`)
      .then(r => setData(r.data))
      .catch(e => setErr(apiErrorMessage(e, "Couldn't load journey.")));
  }, [sessionId]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="journey-detail-modal">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2 mb-4">
          <div>
            <h3 className="text-lg font-semibold">Session journey</h3>
            <div className="text-xs text-slate-500 font-mono mt-0.5">{sessionId}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
        </div>

        {err && <div className="text-red-600 text-sm">{err}</div>}
        {!data && !err && <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>}

        {data && (
          <>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {data.hops.map((h, i) => (
                <div key={i} className="flex items-center gap-1">
                  <div className="px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-xs">
                    <div className="font-medium">{h.site}</div>
                    <div className="text-[10px] text-emerald-700">{h.events} event{h.events !== 1 ? "s" : ""}</div>
                  </div>
                  {i < data.hops.length - 1 && <span className="text-slate-400 text-lg">→</span>}
                </div>
              ))}
            </div>

            {data.converted && (
              <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2 mb-4">
                <span className="font-medium text-emerald-900">Converted:</span> {data.outcomes.join(", ")}
              </div>
            )}

            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Full event timeline</div>
            <div className="space-y-1 text-xs">
              {data.steps.map((s, i) => (
                <div key={i} className="flex items-start gap-2 py-1 border-b border-slate-100">
                  <div className="w-32 flex-shrink-0 text-slate-500">{new Date(s.ingested_at).toLocaleTimeString()}</div>
                  <Badge variant="outline" className="text-[10px] flex-shrink-0">{s.site_slug}</Badge>
                  <div className="flex-1">
                    <span className="font-medium">{s.event}</span>
                    {s.page_path && <span className="text-slate-500 ml-1">· {s.page_path}</span>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

